import PageData from "../../PageData";
import Modal from "../../components/modals/Modal";
import AdFormService from "../../services/AdFormService";
import {Section, SectionId} from "../../services/Section";
import UserContactDataStorageService from "../../services/UserContactDataStorageService";
import WhatsappService from "../../services/WhatsappService";
import FormValidations from "../../forms/validator/FormValidations";
import {getAdIdFromElement} from "./SerpUtils";

export class MultiEnquiryWhatsapp {
    private readonly pageData: PageData;
    private readonly whatsappModal: Modal;
    private readonly phoneInput: HTMLInputElement;
    private readonly phoneError: HTMLSpanElement;
    private readonly adFormService: AdFormService;
    private readonly userContactDataStorageService: UserContactDataStorageService;
    private currentListingId: string = "";
    private clickedButton: HTMLButtonElement | null = null;
    private readonly _whatsappSection: string = Section.SECTION_SEARCH_AD_VIEW_WHATSAPP;
    private readonly _whatsappMultiEnquirySection: string = Section.SECTION_SERP_AD_WHATSAPP_MULTIENQUIRY;
    private readonly _whatsappModalElementId: string = 'email-inquiry-modal-chat-options';
    private readonly _whatsappMultienquiryContainerClassName: string = 'js-whatsapp-multi-enquiry-container';
    private readonly _messageElementId: string = 'email-inquiry-modal-message';
    private readonly originalMessage: string = document.getElementById('email-inquiry-modal-message')!.innerText;

    constructor(pageData: PageData) {
        this.pageData = pageData;
        this.whatsappModal = new Modal({
            pageData,
            element: document.getElementById(this._whatsappModalElementId)!,
            closeTrigger: document.getElementById('close-email-inquiry-modal-chat-options')!,
            isNestoriaMobile: false,
            onOpen: this.onOpen,
            onClose: this.onClose,
        });
        this.phoneInput = document.getElementById('phone-input-chat-options') as HTMLInputElement;
        this.phoneError = document.getElementById('phone-error-chat-options') as HTMLSpanElement;
        this.adFormService = new AdFormService();
        this.userContactDataStorageService = new UserContactDataStorageService();
        this.originalMessage = document.getElementById(this._messageElementId)!.innerText;
        document.getElementById('submit-button-chat-options')?.addEventListener('click', this.submitModal);
        const whatsappButtons = document.getElementsByClassName('js-whatsapp');
        for (let i = 0; i < whatsappButtons.length; i++) {
            whatsappButtons[i].addEventListener("click", this.openModalWhatsapp);
        }
        if (this.phoneInput) this.phoneInput.addEventListener('keyup', this.validatePhone);
    }

    private submitModal = (event: Event) => {
        event.stopImmediatePropagation();
        if (!this.validatePhone()) return;
        const phone = this.getPhone();
        const win = window.open(this.clickedButton!.value, '_blank');
        if (win) win.focus();
        this.resetEnquiryMessage();
        this.adFormService.applyChatOptionDisplay(this.clonePageDataWith(this.currentListingId), this._whatsappSection!, {phone: phone});
        this.displayMultiEnquiry();
    };

    private getPhone() {
        let fieldValue = "";
        if (this.phoneInput && this.phoneInput.value) {
            this.userContactDataStorageService.storeUserInputValue(this.phoneInput, 'phone');
            fieldValue = this.phoneInput.value;
        }
        return fieldValue;
    }

    private displayMultiEnquiry() {
        const multiEnquiryContainer = this.getMultiEnquiryContainer()!;
        if (multiEnquiryContainer.innerHTML.trim() === '') return;
        const chatOptionsForm = this.getChatOptionsForm();
        chatOptionsForm.classList.add('hidden');
        multiEnquiryContainer.classList.remove('hidden');
    }

    private hideMultiEnquiry() {
        const multiEnquiryContainer = this.getMultiEnquiryContainer()!;
        if (multiEnquiryContainer.classList.contains('hidden')) return;
        const chatOptionsForm = this.getChatOptionsForm();
        chatOptionsForm.classList.remove('hidden');
        multiEnquiryContainer.classList.add('hidden');
    }

    private getChatOptionsForm() {
        return document.querySelector('.js-chat-options-form')!;
    }

    private getMultiEnquiryContainer() {
        return document.querySelector(`#${this._whatsappModalElementId} .${this._whatsappMultienquiryContainerClassName}`);
    }

    private onOpen = (listingId: string | undefined) => {
        this.userContactDataStorageService.fillInputsWithStoredValues(
            {input: this.phoneInput, type: 'phone'}
        );

        this.updateMessage();
        this.sendWhatsappApplyImpression(listingId);
        this.multiEnquiryWhatsapp(listingId);
    };

    private multiEnquiryWhatsapp(listingId: string | undefined) {
        const container = this.getMultiEnquiryContainer();
        if (!listingId || !container) return;
        this.adFormService.retrieveWhatsAppMultiEnquirySimilars({
            id: listingId,
            sectionId: SectionId.SECTION_SERP_AD_WHATSAPP_MULTIENQUIRY
        })
            .then((whatsappMultiEnquirySimilarsHtml) => {
                if (whatsappMultiEnquirySimilarsHtml.trim() === '') return;
                container.innerHTML = whatsappMultiEnquirySimilarsHtml;
                let listingIds: string[] = [];
                container.querySelectorAll('.js-whatsapp-multienquiry').forEach((multiEnquiryElement) => {
                    const multiEnquiryListingId: string = multiEnquiryElement.getAttribute('data-listing-id')!;
                    listingIds.push(multiEnquiryListingId);
                    this.adFormService.applyImpressionChatOptionDisplay(this.clonePageDataWith(multiEnquiryListingId), this._whatsappMultiEnquirySection, 1);
                    multiEnquiryElement.addEventListener('click', (ev) => {
                        const phone: string = this.userContactDataStorageService.getUserPhone();
                        this.adFormService.applyChatOptionDisplay(this.clonePageDataWith(multiEnquiryListingId),
                            this._whatsappMultiEnquirySection,
                            {phone: phone},
                            {trackPermissionSendCommunications: false}
                        );
                        const win = window.open(multiEnquiryElement.getAttribute('data-url')!, '_blank');
                        if (win) win.focus();
                    });
                });
                if (listingIds.length > 0) {
                    this.adFormService.listingImpression({
                        pageViewId: this.pageData.pageViewId,
                        propertyAdIds: listingIds,
                        section: this._whatsappMultiEnquirySection as Section,
                    });
                }
            });
    }

    private sendWhatsappApplyImpression(listingId: string | undefined) {
        this.adFormService.applyImpressionChatOptionDisplay(this.clonePageDataWith(listingId), this._whatsappSection, 2);
    }

    private clonePageDataWith(listingId: string | undefined) {
        return {
            ...this.pageData,
            id: listingId || "",
        };
    }

    private updateMessage() {
        document.getElementById(this._messageElementId)!.innerText = document.getElementById(this._messageElementId)!.innerText.replace('{social_network}', "Whatsapp");
    }

    private onClose = () => {
        this.resetErrors();
        this.resetEnquiryMessage();
        this.hideMultiEnquiry();
    };

    private resetEnquiryMessage() {
        document.getElementById(this._messageElementId)!.innerText = this.originalMessage;
    }

    private resetErrors = () => {
        if (this.phoneError) this.phoneError.classList.remove('active');
        if (this.phoneInput) this.phoneInput.classList.remove('invalid');
    }

    private openModalWhatsapp = (event: Event) => {
        event.stopPropagation();
        event.preventDefault();

        let sectionId = 1;
        let adId: string = this.getAdIdFromEvent(event)
        this.currentListingId = adId;
        this.clickedButton = (event.target as HTMLElement).closest("button")

        new WhatsappService().getWhatsappData(
            sectionId,
            this.pageData.chatsData,
            this.pageData.country,
            this.pageData.device,
            adId,
            this.pageData.pageViewId
        )
            .then(response => this.manageWhatsappResponse(response, adId))
            .catch(() => {
                this.showWhatsappModal(adId);
            });
    }

    private manageWhatsappResponse = (chatResponse: Response, adId: string) => {
        if (chatResponse.status !== 200) {
            this.showWhatsappModal(adId);
            return;
        }

        chatResponse.json().then((chatResponseJson) => {
            const {url} = chatResponseJson.data;
            if (this.pageData.device === 'DESKTOP') {
                const chatTargetAnchor = document.createElement('a');
                chatTargetAnchor.target = '_blank';
                chatTargetAnchor.href = url;
                chatTargetAnchor.click();
            } else {
                document.location.href = url;
            }
        });
    }

    private showWhatsappModal = (id: string | undefined) => {
        this.updateMessage();
        this.whatsappModal.open(id)();
    }

    private showError = (errorText: HTMLSpanElement, input: HTMLInputElement, onShowErrorMessage: ((errorText: HTMLSpanElement) => void) = () => {
    }) => {
        if (errorText) {
            onShowErrorMessage(errorText);
            errorText.classList.add('active');
        }
        if (input) input.classList.add('invalid');
    };

    private validatePhone = () => {
        const existsPhoneInput = this.phoneInput !== null;
        if (existsPhoneInput) {
            const isEmpty = FormValidations.isEmpty(this.phoneInput.value);
            const isValidPhone = FormValidations.isValidPhone(this.phoneInput.value);
            const isValid = !isEmpty && isValidPhone;
            this.handleError(isValid, this.phoneError, this.phoneInput, this.hasMultipleErrorMessages(), this.onShowPhoneErrorMessage(isEmpty, isValidPhone));
            return isValid;
        }
        return true;
    }

    private onShowPhoneErrorMessage(isEmpty: boolean, isValidPhone: boolean) {
        if (this.hasMultipleErrorMessages()) {
            let errorMessage = this.getPhoneErrorMessageToShow(isEmpty, isValidPhone);
            return (errorText: HTMLSpanElement) => {
                errorText.innerHTML = errorMessage
            };
        } else {
            return () => {
            };
        }
    }

    private getPhoneErrorMessageToShow(isEmpty: boolean, isValidPhone: boolean) {
        if (isEmpty && this.pageData.validation?.errorMessages.phone.empty !== undefined) {
            return this.pageData.validation.errorMessages.phone.empty;
        } else if (!isValidPhone && this.pageData.validation?.errorMessages.phone.incorrectFormat !== undefined) {
            return this.pageData.validation.errorMessages.phone.incorrectFormat;
        }
        return '';
    }

    private hasMultipleErrorMessages() {
        return this.pageData.validation && this.pageData.validation.hasMultipleErrorMessages;
    }

    private handleError = (isValid: boolean, errorText: HTMLSpanElement, input: HTMLInputElement, hasMultipleErrorMessages: boolean = false, onShowErrorMessage: ((errorText: HTMLSpanElement) => void) = () => {
    }) => {
        const hasActiveError = this.hasErrorActive(input, errorText);
        if (isValid) {
            if (hasActiveError) if (errorText) errorText.classList.remove('active');
            if (input) input.classList.remove('invalid');
        } else if (!hasActiveError || hasMultipleErrorMessages) {
            this.showError(errorText, input, onShowErrorMessage);
        }
    }

    private hasErrorActive = (input: HTMLInputElement, errorText: HTMLSpanElement) => {
        let hasError = false;
        if (errorText) hasError = errorText.classList.contains('active');
        if (input) hasError = input.classList.contains('invalid');
        return hasError;
    }

    private getAdIdFromEvent = (event: Event): string => {
        const target = event.target;
        if (target instanceof Element) {
            return getAdIdFromElement(target);
        }
        return "";
    }
}